import React, { useEffect, useMemo, useRef } from 'react';
import { addEcommerceEventToDataLayer } from './ga4DataLayer';
if (typeof window !== 'undefined')
    require('intersection-observer');
const VIEW_ITEM_LIST_VISIBLE_DELAY = 1800;
const VIEW_ITEM_LIST_BUFFER_DELAY = 1800;
let viewItemListBuffer = null;
let viewItemListBufferTimer = null;
const trackedViewItemListItems = new Set();
let cachedViewItemListEvent = null;
let cachedViewItemListItemsByKey = null;
export function trackVisibleViewItemList(listId, listName, items) {
    if (!items || items.length === 0)
        return;
    addEcommerceEventToDataLayer({
        event: "view_item_list",
        ecommerce: {
            item_list_id: listId,
            item_list_name: listName,
            items
        }
    });
}
function getViewItemListCommerce(event) {
    return event && event.ecommerce ? event.ecommerce : null;
}
function getViewItemListContext(event) {
    const ecommerce = getViewItemListCommerce(event);
    const firstItem = ecommerce && ecommerce.items && ecommerce.items[0];
    const listId = ecommerce && (ecommerce.item_list_id || (firstItem === null || firstItem === void 0 ? void 0 : firstItem.item_list_id));
    const listName = ecommerce && (ecommerce.item_list_name || (firstItem === null || firstItem === void 0 ? void 0 : firstItem.item_list_name));
    if (!listId || !listName)
        return null;
    return {
        listId,
        listName,
    };
}
function getViewItemListItems(event) {
    const ecommerce = getViewItemListCommerce(event);
    return ecommerce && Array.isArray(ecommerce.items) ? ecommerce.items : [];
}
function normalizeGa4Key(value) {
    return (value || '').toLowerCase();
}
function getGa4ItemKey(item) {
    return normalizeGa4Key(`${item.item_id}_${item.item_variant || ''}`);
}
function getViewItemListItemsByKey(event) {
    if (cachedViewItemListEvent === event && cachedViewItemListItemsByKey) {
        return cachedViewItemListItemsByKey;
    }
    const itemsByKey = new Map();
    getViewItemListItems(event).forEach(item => {
        itemsByKey.set(normalizeGa4Key(item.item_id), item);
        itemsByKey.set(getGa4ItemKey(item), item);
    });
    cachedViewItemListEvent = event;
    cachedViewItemListItemsByKey = itemsByKey;
    return itemsByKey;
}
function getTrackedGa4ItemKey(context, item, trackingKey) {
    const pageKey = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '';
    return normalizeGa4Key(`${pageKey}|${trackingKey || ''}|${context.listId}|${context.listName}|${item.item_id}|${item.item_variant || ''}`);
}
function flushViewItemListBuffer() {
    if (viewItemListBufferTimer) {
        clearTimeout(viewItemListBufferTimer);
        viewItemListBufferTimer = null;
    }
    if (!viewItemListBuffer || viewItemListBuffer.items.length === 0) {
        viewItemListBuffer = null;
        return;
    }
    trackVisibleViewItemList(viewItemListBuffer.context.listId, viewItemListBuffer.context.listName, viewItemListBuffer.items);
    viewItemListBuffer = null;
}
export function cancelPendingViewItemListTracking() {
    if (viewItemListBufferTimer) {
        clearTimeout(viewItemListBufferTimer);
        viewItemListBufferTimer = null;
    }
    viewItemListBuffer = null;
}
function queueVisibleViewItemListItem(context, item, trackingKey) {
    if (!item)
        return;
    if (viewItemListBuffer &&
        (viewItemListBuffer.context.listId !== context.listId ||
            viewItemListBuffer.context.listName !== context.listName)) {
        flushViewItemListBuffer();
    }
    if (!viewItemListBuffer) {
        viewItemListBuffer = {
            context,
            items: [],
        };
    }
    const trackedKey = getTrackedGa4ItemKey(context, item, trackingKey);
    if (trackedViewItemListItems.has(trackedKey))
        return;
    trackedViewItemListItems.add(trackedKey);
    viewItemListBuffer.items.push(item);
    if (viewItemListBufferTimer)
        clearTimeout(viewItemListBufferTimer);
    viewItemListBufferTimer = setTimeout(flushViewItemListBuffer, VIEW_ITEM_LIST_BUFFER_DELAY);
}
function getProductGa4ItemCandidates(product, selectedVariant) {
    const productCode = product.code || product.articleNumber;
    const variantUnit = (selectedVariant === null || selectedVariant === void 0 ? void 0 : selectedVariant.unitShortName) || product.unit;
    const variantCode = (selectedVariant === null || selectedVariant === void 0 ? void 0 : selectedVariant.code) || (productCode && variantUnit ? `${productCode}_${variantUnit}` : null);
    return [variantCode, productCode && variantUnit ? `${productCode}_${variantUnit}` : null, productCode]
        .filter(Boolean)
        .map(value => normalizeGa4Key(value));
}
function getVisibleProductGa4Item(event, product, selectedVariant) {
    const itemsByKey = getViewItemListItemsByKey(event);
    const candidates = getProductGa4ItemCandidates(product, selectedVariant);
    return candidates.map(candidate => itemsByKey.get(candidate)).filter(Boolean)[0];
}
export function getSelectItemEventFromViewItemList(event, product, selectedVariant) {
    const context = getViewItemListContext(event);
    const item = getVisibleProductGa4Item(event, product, selectedVariant);
    if (!context || !item)
        return null;
    return {
        event: 'select_item',
        ecommerce: {
            item_list_id: context.listId,
            item_list_name: context.listName,
            items: [item],
        },
    };
}
export function getGa4EventContextFromViewItemList(event, product, selectedVariant) {
    const context = getViewItemListContext(event);
    const item = getVisibleProductGa4Item(event, product, selectedVariant);
    if (!context || !item)
        return undefined;
    return {
        itemListId: item.item_list_id || context.listId,
        itemListName: item.item_list_name || context.listName,
        locationId: item.location_id,
        index: item.index,
    };
}
export function ViewItemListCardTracker({ viewItemListEvent, product, selectedVariant, trackingKey, children, }) {
    const elementRef = useRef(null);
    const visibilityTimerRef = useRef(null);
    const context = useMemo(() => getViewItemListContext(viewItemListEvent), [viewItemListEvent]);
    const item = useMemo(() => viewItemListEvent && getVisibleProductGa4Item(viewItemListEvent, product, selectedVariant), [viewItemListEvent, product, selectedVariant]);
    useEffect(() => {
        const trackerElement = elementRef.current;
        const element = (trackerElement === null || trackerElement === void 0 ? void 0 : trackerElement.firstElementChild) || trackerElement;
        if (!element || !context || !item || typeof IntersectionObserver === 'undefined')
            return;
        const clearVisibilityTimer = () => {
            if (visibilityTimerRef.current) {
                clearTimeout(visibilityTimerRef.current);
                visibilityTimerRef.current = null;
            }
        };
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    if (visibilityTimerRef.current)
                        return;
                    visibilityTimerRef.current = setTimeout(() => {
                        queueVisibleViewItemListItem(context, item, trackingKey);
                        visibilityTimerRef.current = null;
                    }, VIEW_ITEM_LIST_VISIBLE_DELAY);
                }
                else {
                    clearVisibilityTimer();
                }
            });
        }, {
            root: null,
            rootMargin: '0px',
            threshold: [0, 0.5],
        });
        observer.observe(element);
        return () => {
            clearVisibilityTimer();
            observer.disconnect();
        };
    }, [context, item, trackingKey]);
    return React.createElement("div", { ref: elementRef, style: { display: 'contents' } }, children);
}
